/* tslint:disable */
/* eslint-disable */
/* Mocked in-memory implementation of StockCheckClient for local development and UI wiring. */

import { CancelToken } from 'axios';
import {
  SuccessResponse_1OfPagedData_1OfStockCheck,
  SuccessResponse_1OfStockCheckDetails,
  SuccessResponse_1OfIEnumerable_1OfStockCheckTypesResponse,
  SuccessResponse_1OfRAG,
  SuccessResponse_1OfList_1OfCountZonesResponse,
  StockCheck,
  StockCheckStatus,
  CreateCountZoneRequest,
  UpdateCountZoneRequest,
  StockCheckItem,
  SuccessResponse_1OfIEnumerable_1OfStockCheckRAG,
  SuccessResponse_1OfBoolean,
  FileResponse,
  SuccessResponse_1OfBulkAddResponse,
} from '../../types';

/* ---- Helper Functions ---- */

function delay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function successResponse<T>(data: T): { data: T; success: boolean; message: string } {
  return { data, success: true, message: 'Success' };
}

/* ---- Mock Data ---- */

// Helper to get today's date in YYYY-MM-DD format
const getTodayDate = () => new Date().toISOString().split('T')[0];

const mockStockChecks: Partial<StockCheck>[] = [
  {
    id: 1,
    name: 'Weekly Stock Count',
    storeNumber: 'VAP001',
    status: 3, // Pending
    stockCheckType: 1,
    plannedCountDate: getTodayDate(),
  },
  {
    id: 2,
    name: 'Italian Cheese Audit',
    storeNumber: 'VAP001',
    status: 3, // Pending
    stockCheckType: 2,
    plannedCountDate: getTodayDate(),
  },
  {
    id: 3,
    name: 'Full Pantry Count',
    storeNumber: 'VAP001',
    status: 1, // Counting
    stockCheckType: 1,
    plannedCountDate: getTodayDate(),
    startDate: new Date().toISOString(),
  },
  {
    id: 4,
    name: 'Bar Stock Count',
    storeNumber: 'VAP001',
    status: 4, // Pending Approval
    stockCheckType: 2,
    plannedCountDate: getTodayDate(),
    startDate: new Date().toISOString(),
  },
  {
    id: 5,
    name: 'Monthly Full Count',
    storeNumber: 'VAP001',
    status: 2, // Completed
    stockCheckType: 1,
    plannedCountDate: getTodayDate(),
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
  },
  {
    id: 6,
    name: 'Fresh Produce Check',
    storeNumber: 'VAP002',
    status: 3, // Pending
    stockCheckType: 2,
    plannedCountDate: getTodayDate(),
  },
  {
    id: 11,
    name: 'Weekly Protein Count',
    storeNumber: 'FG001',
    status: 3, // Pending
    stockCheckType: 1,
    plannedCountDate: getTodayDate(),
  },
  {
    id: 12,
    name: 'Fresh Produce Check',
    storeNumber: 'FG001',
    status: 3, // Pending
    stockCheckType: 2,
    plannedCountDate: getTodayDate(),
  },
  {
    id: 13,
    name: 'Full Store Count',
    storeNumber: 'FG001',
    status: 1, // Counting
    stockCheckType: 1,
    plannedCountDate: getTodayDate(),
    startDate: new Date().toISOString(),
  },
  {
    id: 14,
    name: 'Oil & Seasonings Audit',
    storeNumber: 'FG001',
    status: 4, // Pending Approval
    stockCheckType: 2,
    plannedCountDate: getTodayDate(),
    startDate: new Date().toISOString(),
  },
  {
    id: 15,
    name: 'End of Week Count',
    storeNumber: 'FG001',
    status: 2, // Completed
    stockCheckType: 1,
    plannedCountDate: getTodayDate(),
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
  },
  {
    id: 16,
    name: 'Bakery & Buns Count',
    storeNumber: 'FG002',
    status: 3, // Pending
    stockCheckType: 2,
    plannedCountDate: getTodayDate(),
  },
];

const mockZones = [
  { id: 1, name: 'Back Storage', storeNumber: 'STORE001', itemCount: 25 },
  { id: 2, name: 'Front Counter', storeNumber: 'STORE001', itemCount: 15 },
  { id: 3, name: 'Walk-in Freezer', storeNumber: 'STORE001', itemCount: 20 },
];

const mockZoneItems = [
  { itemNumber: 'ITEM001', description: 'Whole Milk', zoneId: 1 },
  { itemNumber: 'ITEM002', description: 'White Bread', zoneId: 1 },
];

/* ---- Mocked Client Implementation ---- */

import { IStockCheckClient } from '../../interfaces';
export class StockCheckClientMocked implements IStockCheckClient {
  constructor(baseUrl?: string) {}

  async stockcheckGET(
    storeNumber?: string,
    search?: string,
    date?: string,
    status?: StockCheckStatus,
    skip?: number,
    take?: number,
    stockCheckType?: number,
    cancelToken?: CancelToken
  ): Promise<SuccessResponse_1OfPagedData_1OfStockCheck> {
    await delay();
    let filtered = [...mockStockChecks];

    if (storeNumber) filtered = filtered.filter((sc) => sc.storeNumber === storeNumber);
    if (search) {
      filtered = filtered.filter((sc) => sc.name?.toLowerCase().includes(search.toLowerCase()));
    }
    // When status is undefined, show only active (non-completed) items
    if (status !== undefined) {
      filtered = filtered.filter((sc) => sc.status === status);
    } else {
      // Filter out completed items (status 2) when no specific status is requested
      filtered = filtered.filter((sc) => sc.status !== 2);
    }
    if (stockCheckType) filtered = filtered.filter((sc) => sc.stockCheckType === stockCheckType);

    const start = skip || 0;
    const end = start + (take || 10);

    return successResponse({
      data: filtered.slice(start, end),
      totalCount: filtered.length,
      skip: start,
      take: end - start,
    }) as SuccessResponse_1OfPagedData_1OfStockCheck;
  }

  async stockcheckPOST(body?: StockCheck, cancelToken?: CancelToken): Promise<void> {
    await delay();
    if (body) {
      mockStockChecks.push({
        ...body,
        id: mockStockChecks.length + 1,
        status: 0,
        plannedCountDate: new Date().toISOString().split('T')[0],
      });
    }
  }

  async stockcheckGET2(id: number, cancelToken?: CancelToken): Promise<SuccessResponse_1OfStockCheckDetails> {
    await delay();
    // Get the current stock check from the array (which may have been updated by start())
    const stockCheck = mockStockChecks.find((sc) => sc.id === id) || mockStockChecks[0];
    console.log(`[Mock] Getting stock check details for ${id}, status: ${stockCheck.status}`);
    
    // Build franchise-specific items based on storeNumber
    const storeNumber = stockCheck.storeNumber || '';
    let mockItems: StockCheckItem[];

    if (storeNumber.startsWith('VAP')) {
      mockItems = [
        { id: 1, stockCheckId: id, itemNumber: 'VAP-FLOUR-001', description: '00 Flour (Caputo) 25kg', category: 'Flour & Dry Goods', productType: 'Ingredient', primaryUom: 'Bag', quantityOnHand: 4, costPriceOnHand: 18.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 1, stockCheckItemId: 1, uom: 'BAG', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 2, stockCheckId: id, itemNumber: 'VAP-EGGS-001', description: 'Free Range Eggs (Large) 360pk', category: 'Fresh Dairy', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 3, costPriceOnHand: 42.00, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 2, stockCheckItemId: 2, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 3, stockCheckId: id, itemNumber: 'VAP-GUANCIALE-001', description: 'Guanciale (Cured Pork Cheek) 2kg', category: 'Italian Charcuterie', productType: 'Ingredient', primaryUom: 'Each', quantityOnHand: 5, costPriceOnHand: 28.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 3, stockCheckItemId: 3, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 4, stockCheckId: id, itemNumber: 'VAP-PECORINO-001', description: 'Pecorino Romano DOP 5kg', category: 'Italian Cheese', productType: 'Ingredient', primaryUom: 'Each', quantityOnHand: 2, costPriceOnHand: 65.00, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 4, stockCheckItemId: 4, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 5, stockCheckId: id, itemNumber: 'VAP-MOZZ-001', description: 'Fior di Latte Mozzarella 1kg', category: 'Italian Cheese', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 8, costPriceOnHand: 12.80, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 5, stockCheckItemId: 5, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 6, stockCheckId: id, itemNumber: 'VAP-TOMATO-001', description: 'San Marzano Tomatoes 2.5kg', category: 'Tinned Goods', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 12, costPriceOnHand: 4.80, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 10, itemValues: [{ id: 6, stockCheckItemId: 6, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 7, stockCheckId: id, itemNumber: 'VAP-OLIVE-001', description: 'Extra Virgin Olive Oil 5L', category: 'Oils & Vinegars', productType: 'Ingredient', primaryUom: 'Each', quantityOnHand: 3, costPriceOnHand: 24.00, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 7, stockCheckItemId: 7, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 8, stockCheckId: id, itemNumber: 'VAP-PROSECCO-001', description: 'Prosecco DOC 750ml', category: 'Wines & Spirits', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 6, costPriceOnHand: 6.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 8, stockCheckItemId: 8, uom: 'CS', conversionToPrimary: 6, countQuantity: undefined }, { id: 18, stockCheckItemId: 8, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
      ];
    } else if (storeNumber.startsWith('FG')) {
      mockItems = [
        { id: 1, stockCheckId: id, itemNumber: 'FG-BEEF-001', description: 'Fresh Ground Beef Patties 4oz x48', category: 'Fresh Beef', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 8, costPriceOnHand: 68.00, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 1, stockCheckItemId: 1, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 2, stockCheckId: id, itemNumber: 'FG-POTATO-001', description: 'Fresh Potatoes (Maris Piper) 25kg', category: 'Fresh Produce', productType: 'Ingredient', primaryUom: 'Bag', quantityOnHand: 12, costPriceOnHand: 8.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 2, stockCheckItemId: 2, uom: 'BAG', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 3, stockCheckId: id, itemNumber: 'FG-PEANUT-OIL-001', description: '100% Refined Peanut Oil 20L', category: 'Oils', productType: 'Ingredient', primaryUom: 'Each', quantityOnHand: 4, costPriceOnHand: 32.00, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 3, stockCheckItemId: 3, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 4, stockCheckId: id, itemNumber: 'FG-BUNS-001', description: 'Sesame Seed Burger Buns x60', category: 'Bakery', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 6, costPriceOnHand: 18.00, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 4, stockCheckItemId: 4, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 5, stockCheckId: id, itemNumber: 'FG-CHEESE-001', description: 'American Cheese Slices x160', category: 'Dairy', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 4, costPriceOnHand: 22.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 5, stockCheckItemId: 5, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 6, stockCheckId: id, itemNumber: 'FG-BACON-001', description: 'Smoked Back Bacon 2.5kg', category: 'Fresh Meat', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 5, costPriceOnHand: 14.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 6, stockCheckItemId: 6, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 7, stockCheckId: id, itemNumber: 'FG-PEANUTS-001', description: 'Roasted Peanuts (In-Shell) 10kg', category: 'Snacks', productType: 'Ingredient', primaryUom: 'Bag', quantityOnHand: 4, costPriceOnHand: 12.00, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 10, itemValues: [{ id: 7, stockCheckItemId: 7, uom: 'BAG', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 8, stockCheckId: id, itemNumber: 'FG-CAJUN-001', description: 'Five Guys Cajun Seasoning 2kg', category: 'Seasonings', productType: 'Ingredient', primaryUom: 'Each', quantityOnHand: 2, costPriceOnHand: 8.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 8, stockCheckItemId: 8, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
      ];
    } else {
      mockItems = [
        { id: 1, stockCheckId: id, itemNumber: 'SKU-MILK-001', description: 'Whole Milk 2L', category: 'Dairy', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 24, costPriceOnHand: 2.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 1, stockCheckItemId: 1, uom: 'CS', conversionToPrimary: 12, countQuantity: undefined }, { id: 11, stockCheckItemId: 1, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 2, stockCheckId: id, itemNumber: 'SKU-BREAD-001', description: 'White Sliced Bread 800g', category: 'Bakery', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 36, costPriceOnHand: 1.20, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 2, stockCheckItemId: 2, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 3, stockCheckId: id, itemNumber: 'SKU-COFFEE-001', description: 'Premium Coffee Beans 1kg', category: 'Coffee', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 10, costPriceOnHand: 18.50, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 3, stockCheckItemId: 3, uom: 'CS', conversionToPrimary: 10, countQuantity: undefined }, { id: 13, stockCheckItemId: 3, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 4, stockCheckId: id, itemNumber: 'SKU-SUGAR-001', description: 'Granulated Sugar 1kg', category: 'Dry Goods', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 20, costPriceOnHand: 0.85, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 4, stockCheckItemId: 4, uom: 'CS', conversionToPrimary: 1, countQuantity: undefined }] },
        { id: 5, stockCheckId: id, itemNumber: 'SKU-EGGS-001', description: 'Free Range Eggs 12pk', category: 'Dairy', productType: 'Ingredient', primaryUom: 'Case', quantityOnHand: 12, costPriceOnHand: 3.20, countQuantityTotal: 0, outsideTolerance: false, isApproved: false, tolerancePercentage: 5, itemValues: [{ id: 5, stockCheckItemId: 5, uom: 'CS', conversionToPrimary: 12, countQuantity: undefined }, { id: 15, stockCheckItemId: 5, uom: 'EA', conversionToPrimary: 1, countQuantity: undefined }] },
      ];
    }
    
    // Add more complete data for stock check details - items go INSIDE the stockCheck object
    const enrichedStockCheck = {
      ...stockCheck,
      canStartCount: true, // Always allow starting in mock mode for testing
      unlockTimeUtc: undefined, // No lock time in mock mode
      items: mockItems, // Items must be inside the stockCheck object, not at root level
    };
    
    return successResponse({
      stockCheck: enrichedStockCheck,
      hasPosData: true,
      allCountZonesEnabled: true,
    }) as SuccessResponse_1OfStockCheckDetails;
  }

  async stockcheckDELETE(id: number, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const index = mockStockChecks.findIndex((sc) => sc.id === id);
    if (index > -1) {
      mockStockChecks.splice(index, 1);
    }
  }

  async start(id: number, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const stockCheck = mockStockChecks.find((sc) => sc.id === id);
    if (stockCheck) {
      stockCheck.status = 1; // Counting
      stockCheck.startDate = new Date().toISOString();
      console.log(`[Mock] Started stock check ${id}, new status: ${stockCheck.status}`);
    } else {
      console.log(`[Mock] Stock check ${id} not found in start()`);
    }
  }

  async submit(id: number, body?: StockCheckItem[], cancelToken?: CancelToken): Promise<void> {
    await delay();
    const stockCheck = mockStockChecks.find((sc) => sc.id === id);
    if (stockCheck) {
      stockCheck.status = 4; // PendingApproval (not 3 which is Pending)
      stockCheck.endDate = new Date().toISOString();
    }
  }

  async save(id: number, body?: StockCheckItem[], cancelToken?: CancelToken): Promise<void> {
    await delay();
    const stockCheck = mockStockChecks.find((sc) => sc.id === id);
    if (stockCheck) {
      stockCheck.status = 2;
    }
  }

  async rag2(cancelToken?: CancelToken): Promise<SuccessResponse_1OfRAG> {
    await delay();
    return successResponse({ red: 1, amber: 2, green: 15 }) as SuccessResponse_1OfRAG;
  }

  async import6(body?: StockCheck[], cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async zones(storeNumber: string, cancelToken?: CancelToken): Promise<SuccessResponse_1OfList_1OfCountZonesResponse> {
    await delay();
    const zones = mockZones.filter((z) => z.storeNumber === storeNumber);
    return successResponse(zones) as SuccessResponse_1OfList_1OfCountZonesResponse;
  }

  async zonesPOST(body?: CreateCountZoneRequest, cancelToken?: CancelToken): Promise<void> {
    await delay();
    if (body) {
      mockZones.push({
        id: mockZones.length + 1,
        name: body.name || 'New Zone',
        storeNumber: 'STORE001',
        itemCount: 0,
      });
    }
  }

  async zonesDELETE(id: number, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const index = mockZones.findIndex((z) => z.id === id);
    if (index > -1) {
      mockZones.splice(index, 1);
    }
  }

  async zonesPATCH(id: number, body?: UpdateCountZoneRequest, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const zone = mockZones.find((z) => z.id === id);
    if (zone && body) {
      zone.name = body.name || zone.name;
    }
  }

  async zoneitems(zoneId: number, cancelToken?: CancelToken): Promise<{ data: typeof mockZoneItems }> {
    await delay();
    const items = mockZoneItems.filter((i) => i.zoneId === zoneId);
    return successResponse(items) as { data: typeof mockZoneItems };
  }

  async zoneitemsPOST(zoneId: number, body?: string[], cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async zoneitemsDELETE(zoneId: number, itemNumbers?: string[], cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async g(red: boolean, amber: boolean, green: boolean, cancelToken?: CancelToken): Promise<SuccessResponse_1OfIEnumerable_1OfStockCheckRAG> {
    await delay();
    return successResponse([]) as SuccessResponse_1OfIEnumerable_1OfStockCheckRAG;
  }

  // ====== Auto-generated stub methods ======




























    async savesingleitem(id: number, body?: StockCheckItem | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method savesingleitem not implemented in mocked client');
    }

    async approve(id: number, body?: StockCheckItem[] | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method approve not implemented in mocked client');
    }

    async getatlocation(id: number, locationId: number, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfStockCheckDetails> {
        throw new Error('Method getatlocation not implemented in mocked client');
    }

    async submitatlocation(id: number, locationId: number, body?: StockCheckItem[] | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method submitatlocation not implemented in mocked client');
    }

    async saveatlocation(id: number, locationId: number, body?: StockCheckItem[] | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method saveatlocation not implemented in mocked client');
    }

    async savesingleitematlocation(id: number, locationId: number, body?: StockCheckItem | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method savesingleitematlocation not implemented in mocked client');
    }

    async hasoutstanding(storeNumber?: string | undefined, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfBoolean> {
        throw new Error('Method hasoutstanding not implemented in mocked client');
    }

    async updateitems(id: number, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method updateitems not implemented in mocked client');
    }

    async export3(id: number, cancelToken?: CancelToken | undefined): Promise<FileResponse> {
        throw new Error('Method export3 not implemented in mocked client');
    }

    async bulkadd(id: number, storeNumber?: string | undefined, body?: string[] | undefined, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfBulkAddResponse> {
        throw new Error('Method bulkadd not implemented in mocked client');
    }

    async stockCheckTypes(cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfIEnumerable_1OfStockCheckTypesResponse> {
        throw new Error('Method stockCheckTypes not implemented in mocked client');
    }

}
